<template>
  <div class="catalog-view"  v-if="permissionToReadAttachments">
    <div class="attachment-preview" v-if="attachmentId !== 'null' && !attachmentErrorOnLoad">
      <h5>Archivo adjunto: {{ attachmentId }} </h5>
      <div class="image" v-if="attachmentType === types.IMG">
        <a :href="attachmentUrl" :download="`${attachmentName}.${attachmentExtension}`" class="btn btn-primary btn-sm">Descargar imagen <i class="fa fa-download"></i></a>
        <img :src="attachmentUrl">
      </div>
      <div class="audio" v-if="attachmentType === types.AUDIO">
        <audio controls>
          <source v-bind:src="attachmentUrl" type="audio/mpeg">
          Your browser does not support the audio element.
        </audio>
      </div>
      <div class="video" v-if="attachmentType === types.VIDEO">
        <video controls>
          <source v-bind:src="attachmentUrl">
          Your browser does not support the audio element.
        </video>
      </div>
      <div class="pdf" v-if="attachmentType === types.PDF">
        <iframe v-bind:src="attachmentUrl" width="100%" height="100%" frameBorder="0"/>
      </div>
      <div class="general-file" v-if="attachmentType === types.FILE">
        No se cuenta con una previsualización del archivo.
        <button class="btn btn-primary btn-sm" @click="downloadFile()">Descargar archivo <i class="fa fa-download"></i></button>
      </div>
    </div>
    <div v-else class="attachment-not-found page-wrap d-flex flex-row align-items-center">
      <div class="container">
        <div class="row justify-content-center">
            <div class="col-md-12 text-center">
                <span class="display-1 d-block">{{ attachmentErrorHTTP }}</span>
                <div class="mb-4 lead">{{attachmentErrorMessage}}</div>
                <div class="mb-4 lead" v-for="(error, index) in attachmentErrors" :key="index">
                  {{error}}
                </div>
            </div>
        </div>
      </div>
    </div>
    <a style="display: none;" ref="fileDownload"></a>
    <loading-mask :active="loadingAttachment"/>
  </div>
  <div v-else class="not-sufficent-permissions page-wrap d-flex flex-row align-items-center">
      <div class="container">
        <div class="row justify-content-center">
            <div class="col-md-12 text-center">
                <span class="display-1 d-block">403</span>
                <div class="mb-4 lead">Lo sentimos, no tienes permisos suficientes para consultar este recurso.</div>
            </div>
        </div>
      </div>
  </div>
</template>

<script lang="ts">
import FillgridService from '@/services/FillgridService'
import Vue from 'vue'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import userInfoStore from '@/store/userInfo'
import { PermissionNames } from '@/types/Auth'

// eslint-disable-next-line
enum AttachmentType {
  // eslint-disable-next-line
  IMG = 'image',
  // eslint-disable-next-line
  PDF = 'pdf',
  // eslint-disable-next-line
  AUDIO = 'audio',
  // eslint-disable-next-line
  VIDEO = 'video',
  // eslint-disable-next-line
  FILE = 'file'
}

export default Vue.extend({
  components: {
    LoadingMask
  },
  data () {
    return {
      types: AttachmentType,
      attachmentType: '',
      attachmentUrl: '',
      attachmentSource: {
        blob: '',
        type: ''
      },
      mediaPreview: [ AttachmentType.IMG, AttachmentType.AUDIO, AttachmentType.PDF ] as string[],
      IMAGE_TYPE: [
        'image/gif',
        'image/jpg',
        'image/jpeg',
        'image/png',
        'image/svg+xml',
        'image/x-icon',
        'image/webp'
      ],
      PDF_EXTENSIONS: [
        'application/pdf'
      ],
      AUDIO_TYPE: [
        'audio/wave',
        'audio/vnd.wave',
        'audio/wav',
        'audio/x-wav',
        'audio/x-pn-wav',
        'audio/webm',
        'audio/ogg',
        'audio/midi',
        'audio/mpeg',
        'application/ogg'
      ],
      VIDEO_TYPE: [
        'video/webm',
        'video/ogg',
        'video/mp4'
      ],
      FILE_EXTENSIONS: [
        'application/octet-stream',
        'application/pkcs12',
        'application/vnd.mspowerpoint',
        'application/xhtml+xml',
        'application/xml',
        'application/pdf'
      ],
      loadingAttachment: false,
      attachmentErrorOnLoad: false,
      attachmentErrorMessage: '',
      attachmentErrorHTTP: '',
      attachmentErrors: []
    }
  },
  mounted () {
    this.attachmentErrorOnLoad = false
    this.attachmentErrorMessage = ''
    this.attachmentErrorHTTP = ''
    this.attachmentErrors = []
  },
  created () {
    this.loadAttachment()
  },
  computed: {
    permissionToReadAttachments (): boolean {
      return userInfoStore.hasPermission(PermissionNames.ATTACHMENTS_READ)
    },
    attachmentId (): string {
      return this.$route.params.id
    },
    attachmentWithExtension (): boolean {
      return this.attachmentId.includes('.')
    },
    attachmentName (): string {
      return this.attachmentWithExtension ? this.attachmentId.split('.')[0] : this.attachmentId
    },
    attachmentExtension (): string {
      let extension = ''
      if (this.attachmentWithExtension) {
        extension = this.attachmentId.split('.').pop() || ''
      } else {
        switch (this.attachmentType) {
          case AttachmentType.IMG:
            extension = 'jpg'
            break
          case AttachmentType.AUDIO:
            extension = 'mp3'
            break
          case AttachmentType.VIDEO:
            extension = 'mp4'
            break
          case AttachmentType.PDF:
            extension = 'pdf'
            break
        }
      }
      return extension
    },
    template (): any {
      return this.$route.query.template
    },
    question (): any {
      return this.$route.query.question
    }
  },
  methods: {
    /**
     * Get an attachment by name
     */
    async loadAttachment () {
      this.loadingAttachment = true
      await FillgridService.getAttachment(this.attachmentId, this.template, this.question)
        .then((response: any) => {
          this.attachmentSource = response
          this.attachmentUrl = URL.createObjectURL(response.blob)
          this.attachmentType = this.getAttachmentType(response.type)
        })
        .catch((blobError) => {
          new Blob([blobError.response.data], { type: 'application/json' }).text()
            .then((e: any) => {
              const error = JSON.parse(e)
              this.attachmentErrorOnLoad = true
              this.attachmentErrorHTTP = error.httpError ? error.httpError.status : '404'
              this.attachmentErrorMessage = error.message
              this.attachmentErrors = error.errors
                .map((error: any) => {
                  return error.description
                })
            })
        })
        .finally(() => { this.loadingAttachment = false })
    },
    /**
     * Based on a mimeType return the attachment type to manage with a html tag
     *
     * @param mimeType of the file
     */
    getAttachmentType (mimeType: string): string {
      let attachmentType = AttachmentType.FILE
      if (this.IMAGE_TYPE.includes(mimeType)) {
        return AttachmentType.IMG
      } else if (this.PDF_EXTENSIONS.includes(mimeType)) {
        attachmentType = AttachmentType.PDF
      } else if (this.AUDIO_TYPE.includes(mimeType)) {
        attachmentType = AttachmentType.AUDIO
      } else if (this.VIDEO_TYPE.includes(mimeType)) {
        attachmentType = AttachmentType.VIDEO
      } else if (this.FILE_EXTENSIONS.includes(mimeType)) {
        attachmentType = AttachmentType.FILE
      }
      return attachmentType
    },
    /**
     * Download a file that isn't a IMAGE, AUDIO, VIDEO, PDF
     */
    downloadFile () {
      const name = this.attachmentId
      const nameParts = name.split('.')
      let element = this.$refs.fileDownload as any
      element.href = this.attachmentUrl
      element.target = '_blank'
      element.download = nameParts.length > 1 ? name : `${name}.${this.attachmentSource.type}`
      element.click()
    }
  }
})
</script>

<style lang="scss" scoped>
.attachment-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  width: 100%;
  height: 100%;
  overflow-y: auto;

  h5 {
    position: sticky;
    top: 0;
    background-color: rgba($color: #fff, $alpha: 0.75);
    padding: 0.5rem;
    border-radius: 4px;
  }

  .image {
    a {
      position: sticky;
      top: 0;
    }
    img {
      width: 100%;
    }
  }

  .pdf {
    width: 100%;
    height: 100%;
  }
}
.not-sufficent-permissions,
.attachment-not-found {
  height: 100%;
}
</style>
